import { isNonEmptyArray } from '@apollo/client/utilities';
import { useTypedQuery } from '../lib/generated/zeus/apollo';
import type { StoredUser } from './types';

export const queryPermissions = 'queryPermission';

export type Permissions = {
  readonly isPowerUser: boolean;
  readonly teamManageNonLocalImt: boolean;
  readonly teamSetIon: boolean;
  readonly teamManageRosterAndTeam: boolean;
  readonly teamDelete: boolean;
  readonly teamRoleManage: boolean;
} & {
  readonly reload: () => Promise<void>;
};

export const usePermission = (teamId?: string, loggedInUser?: StoredUser) => {
  const powerUserPermissions = useTypedQuery(
    {
      permissions_team: [
        {
          where: {
            external_id: {
              _eq: loggedInUser?.external_id ?? '',
            },
            action: {
              _eq: 'view',
            },
            target_entity: { _eq: 'team' },
            is_power_user: {
              _eq: true,
            },
          },
        },
        { external_id: true },
      ],
    },
    {
      apolloOptions: {
        skip: loggedInUser?.external_id === undefined,
      },
    }
  );

  const teamPermissionEntries = useTypedQuery(
    {
      permissions_team: [
        {
          where: {
            external_id: {
              _eq: loggedInUser?.external_id ?? '',
            },
            action: {
              _eq: 'edit',
            },
            target_entity: { _eq: 'team' },
            _or: [
              {
                target_entity_id: {
                  _eq: teamId,
                },
              },
              {
                // If the permission entry target entry id is null, it represent wildcard where
                // the access is for all teams.
                target_entity_id: {
                  _is_null: true,
                },
              },
            ],
          },
        },
        { external_id: true },
      ],
    },
    {
      apolloOptions: {
        skip: loggedInUser?.external_id === undefined,
      },
    }
  );

  const teamRoleAssignmentPermissionEntries = useTypedQuery(
    {
      permissions_team_role_assignment: [
        {
          where: {
            external_id: {
              _eq: loggedInUser?.external_id ?? '',
            },
            action: {
              _eq: 'create',
            },
            target_entity: { _eq: 'team_role_assignment' },
            _or: [
              {
                team_id: {
                  _eq: teamId,
                },
              },
              {
                // If the target team id is null, it represent wildcard where the access is for all teams.
                team_id: {
                  _is_null: true,
                },
              },
            ],
          },
        },
        { external_id: true },
      ],
    },
    {
      apolloOptions: {
        skip: loggedInUser?.external_id === undefined,
      },
    }
  );

  const isPowerUser = isNonEmptyArray(
    powerUserPermissions.data?.permissions_team
  );

  const teamManageRosterAndTeam = isNonEmptyArray(
    teamPermissionEntries.data?.permissions_team
  );

  const teamAssignTeamRole = isNonEmptyArray(
    teamRoleAssignmentPermissionEntries.data?.permissions_team_role_assignment
  );

  return {
    isPowerUser,
    teamManageNonLocalImt: isPowerUser,
    teamSetIon: isPowerUser,
    teamManageRosterAndTeam,
    teamAssignTeamRole,
    teamDelete: isPowerUser,
    teamRoleManage: isPowerUser,
    reload: async () => {
      await powerUserPermissions.refetch();
      await teamPermissionEntries.refetch();
      await teamRoleAssignmentPermissionEntries.refetch();
    },
  };
};
